import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { gestApi, useGestAuth } from "./GestAuthContext";

/**
 * Mobile-first entry form used at /gestione/produzione and /gestione/posa.
 * Optimized for smartphone/tablet use by warehouse & installation crews:
 *   - Big cantiere cards instead of a dropdown
 *   - Number chosen via +/- steppers with large tap targets
 *   - Sticky footer with confirm CTA
 *   - Zero economic data ever surfaced to these roles
 */
const lightColour = { green: "#16a34a", yellow: "#eab308", red: "#dc2626" };

export default function GestAvanzamento({ mode }) {
  const { user } = useGestAuth();
  const isProd = mode === "produzione";
  const endpoint = isProd ? "/produzione" : "/posa";
  const countField = isProd ? "n_prodotti" : "n_posati";
  const verb = isProd ? "prodotti" : "posati";
  const title = isProd ? "Avanzamento Produzione" : "Avanzamento Posa";
  const sub = isProd ? "Registra i pezzi prodotti oggi" : "Registra i pezzi posati oggi";
  const accent = "#1D3F94"; // HGW brand blue

  const [cantieri, setCantieri] = useState([]);
  const [rows, setRows] = useState([]);
  const [cid, setCid] = useState("");
  const [n, setN] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadCantieri = async () => {
    const { data } = await gestApi.get("/cantieri");
    const relevant = data.filter((c) => ["in_corso", "preventivo"].includes(c.stato));
    setCantieri(relevant);
  };
  const loadRows = async () => {
    const { data } = await gestApi.get(endpoint);
    setRows(data);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadCantieri(), loadRows()]).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [mode]);

  const selected = useMemo(() => cantieri.find((c) => c.id === cid), [cantieri, cid]);
  const totali = selected?.n_infissi_totali || 0;
  const kpi = selected?.kpi;
  const cur = isProd ? (kpi?.prodotti_totali || 0) : (kpi?.posati_totali || 0);
  const pct = isProd ? (kpi?.prod_pct || 0) : (kpi?.posa_pct || 0);
  const remaining = Math.max(0, totali - cur);

  // Clamp n against the remaining count so the crew can't overshoot by mistake
  const maxN = remaining > 0 ? remaining : 999;
  const setNSafe = (v) => setN(Math.max(1, Math.min(maxN, Math.floor(Number(v) || 1))));

  const submit = async () => {
    if (busy || !cid || !n) return;
    setBusy(true);
    try {
      await gestApi.post(endpoint, { cantiere_id: cid, [countField]: Number(n), note });
      toast.success(`✓ +${n} pezzi ${verb}`);
      setN(1);
      setNote("");
      await Promise.all([loadRows(), loadCantieri()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="pb-40 md:pb-8" data-testid={`gest-${mode}-page`}>
      {/* header */}
      <div className="p-5 md:p-8 max-w-[900px] mx-auto">
        <div className="text-[11px] uppercase tracking-widest text-[#6e6e73]">{title}</div>
        <h1 className="text-[24px] md:text-[32px] font-semibold tracking-[-0.02em] text-[#0a1420]">{sub}</h1>
        <div className="text-[13px] text-[#6e6e73] mt-1">Ciao <span className="font-medium text-[#0a1420]">{user?.name?.split(" ")[0]}</span>, seleziona un cantiere e inserisci quanti pezzi hai fatto.</div>
      </div>

      {/* content */}
      <div className="px-5 md:px-8 max-w-[900px] mx-auto space-y-6">
        {/* Step 1: cantieri */}
        <section>
          <div className="text-[11px] uppercase tracking-widest text-[#6e6e73] mb-2">1. Scegli il cantiere</div>
          {loading ? (
            <div className="text-[13px] text-[#6e6e73]">Caricamento…</div>
          ) : cantieri.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center text-[13px] text-[#6e6e73] border border-black/[0.04]">Nessun cantiere disponibile.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {cantieri.map((c) => {
                const active = c.id === cid;
                const p = isProd ? (c.kpi?.prod_pct || 0) : (c.kpi?.posa_pct || 0);
                const rem = Math.max(0, (c.n_infissi_totali || 0) - (isProd ? (c.kpi?.prodotti_totali || 0) : (c.kpi?.posati_totali || 0)));
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCid(c.id); setN(1); }}
                    data-testid={`gest-${mode}-cantiere-card-${c.id}`}
                    className={`text-left p-4 rounded-2xl transition-all border-2 ${
                      active
                        ? "border-[#1D3F94] bg-[#1D3F94]/[0.04] shadow-md"
                        : "border-black/[0.04] bg-white hover:border-black/20 active:scale-[0.98]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: lightColour[c.kpi?.light || "green"] }} />
                          <div className="text-[15px] font-semibold text-[#0a1420] truncate">{c.nome}</div>
                        </div>
                        <div className="text-[12px] text-[#6e6e73] mt-0.5 truncate">{c.cliente}</div>
                      </div>
                      {active && (
                        <div className="w-6 h-6 rounded-full bg-[#1D3F94] text-white flex items-center justify-center shrink-0">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 h-1.5 bg-black/[0.06] rounded-full overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${Math.min(100, p)}%`, backgroundColor: accent }} />
                    </div>
                    <div className="mt-1.5 flex items-center justify-between text-[11px] text-[#6e6e73]">
                      <span>{p}% {verb}</span>
                      <span className="font-medium text-[#0a1420]">{rem} rimasti</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Step 2: quantità */}
        {selected && (
          <section data-testid={`gest-${mode}-quantita-section`}>
            <div className="text-[11px] uppercase tracking-widest text-[#6e6e73] mb-2">2. Quanti pezzi hai {verb} oggi?</div>
            <div className="bg-white rounded-2xl p-5 border border-black/[0.04] shadow-sm">
              {/* progress mini */}
              <div className="flex items-center justify-between text-[12px] mb-3 text-[#6e6e73]">
                <span>{cur} / {totali} pezzi ({pct}%)</span>
                <span className="font-medium text-[#0a1420]">{remaining} rimasti</span>
              </div>

              {/* Big stepper */}
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setNSafe(n - 1)}
                  disabled={n <= 1 || busy}
                  data-testid={`gest-${mode}-dec`}
                  aria-label="Diminuisci"
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#f5f7fb] active:bg-[#e9edf3] flex items-center justify-center text-[28px] text-[#0a1420] font-light select-none disabled:opacity-30 disabled:cursor-not-allowed"
                >−</button>
                <input
                  type="number"
                  min="1"
                  max={maxN}
                  value={n}
                  onChange={(e) => setNSafe(e.target.value)}
                  data-testid={`gest-${mode}-n`}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="w-24 md:w-32 h-14 md:h-16 text-center text-[36px] md:text-[44px] font-semibold text-[#0a1420] bg-transparent outline-none border-b-2 border-black/10 focus:border-[#1D3F94]"
                />
                <button
                  type="button"
                  onClick={() => setNSafe(n + 1)}
                  disabled={n >= maxN || busy}
                  data-testid={`gest-${mode}-inc`}
                  aria-label="Aumenta"
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#f5f7fb] active:bg-[#e9edf3] flex items-center justify-center text-[28px] text-[#0a1420] font-light select-none disabled:opacity-30 disabled:cursor-not-allowed"
                >+</button>
              </div>

              {/* Quick shortcuts */}
              <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                {[1, 5, 10, 20].filter((v) => v <= maxN).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setNSafe(v)}
                    data-testid={`gest-${mode}-quick-${v}`}
                    className={`px-4 py-2 rounded-full text-[13px] font-medium transition-colors ${
                      n === v ? "bg-[#1D3F94] text-white" : "bg-white text-[#0a1420] border border-black/10 active:bg-[#f5f7fb]"
                    }`}
                  >+{v}</button>
                ))}
                {maxN > 0 && n !== maxN && (
                  <button
                    type="button"
                    onClick={() => setNSafe(maxN)}
                    data-testid={`gest-${mode}-quick-all`}
                    className="px-4 py-2 rounded-full text-[13px] font-medium bg-white text-[#0a1420] border border-black/10 active:bg-[#f5f7fb]"
                  >Tutti ({maxN})</button>
                )}
              </div>

              {/* Note optional */}
              <details className="mt-5">
                <summary className="text-[12px] text-[#6e6e73] cursor-pointer select-none list-none flex items-center gap-1">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  Aggiungi una nota (facoltativa)
                </summary>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid={`gest-${mode}-note`}
                  placeholder="Es. blocco A finestre scorrevoli"
                  className="mt-2 w-full border border-black/10 rounded-lg px-3 py-2.5 text-[14px] resize-none focus:border-[#1D3F94] outline-none"
                />
              </details>
            </div>
          </section>
        )}

        {/* Storico */}
        <section>
          <div className="text-[11px] uppercase tracking-widest text-[#6e6e73] mb-2">Ultimi inserimenti</div>
          <div className="bg-white rounded-2xl border border-black/[0.04] shadow-sm overflow-hidden" data-testid={`gest-${mode}-history`}>
            {rows.length === 0 ? (
              <div className="p-6 text-[13px] text-[#6e6e73] text-center">Ancora nessun avanzamento registrato.</div>
            ) : (
              <div className="divide-y divide-black/5">
                {rows.slice(0, 15).map((r) => {
                  const cn = cantieri.find((c) => c.id === r.cantiere_id)?.nome || "cantiere";
                  return (
                    <div key={r.id} className="px-4 py-3 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-[#1D3F94]/10 text-[#1D3F94] font-semibold text-[15px] flex items-center justify-center shrink-0">
                        +{r[countField]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13.5px] text-[#0a1420] truncate font-medium">{cn}</div>
                        <div className="text-[11.5px] text-[#6e6e73] truncate">
                          {r.autore_name}{r.note ? ` · ${r.note}` : ""}
                        </div>
                      </div>
                      <div className="text-[11px] text-[#6e6e73] shrink-0">
                        {new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Sticky submit CTA (mobile-friendly) */}
      <div className={`fixed left-0 right-0 bottom-16 md:bottom-0 md:left-64 z-30 bg-white border-t border-black/[0.06] shadow-[0_-4px_20px_rgba(0,0,0,0.05)] md:shadow-none transition-transform ${cid ? "translate-y-0" : "translate-y-full"}`}>
        <div className="max-w-[900px] mx-auto p-3 md:p-4">
          <button
            type="button"
            onClick={submit}
            disabled={busy || !cid || !n || remaining === 0}
            data-testid={`gest-${mode}-submit`}
            className="w-full h-14 rounded-2xl bg-[#1D3F94] text-white font-semibold text-[15px] hover:bg-[#173277] active:scale-[0.99] transition-transform disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Salvataggio…
              </>
            ) : remaining === 0 ? (
              <>Cantiere già completato</>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                Registra +{n} {verb}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
